import {IHotelReservation} from '../../interfaces/HotelReservation/IHotelReservation';
import {Reservation} from '../../model/reservation/reservation.model';
import {IIhgConfirmationNumber} from '../../interfaces/HotelReservation/IIhgConfirmationNumber';
import {IStay} from '../../interfaces/HotelReservation/IStay';
import {ReservationStatusType} from '../../model/reservation/reservation-status.model';
import {IGuest} from '../../interfaces/HotelReservation/IGuest';
import {IGuestAddress} from '../../interfaces/HotelReservation/IGuestAddress';
import {IAdditionalGuests} from '../../interfaces/HotelReservation/IAdditionalGuests';
import {ReservationChangeType} from '../../model/reservation/change/reservation-change.model';
import {useDefault} from '../../../common/common';
import {Stay} from '../../model/reservation/stay.model';
import {Room} from '../../model/reservation/room/room.model';
import {Rate} from '../../model/reservation/rate/rate.model';
import {Guest} from '../../model/guest/guest.model';
import {Hotel} from '../../model/hotel/hotel.model';
import {DateUtils} from '../../../common/date.utils';
import {IRateChanges} from '../../interfaces/HotelReservation/IRateChanges';
import {CommunicationPreferences} from '../../model/communication';
import {SpecialNightRate} from '../../model/special-night-rate';

const TWO_DECIMALS = 2;

export class ReservationRequestBuilder {
  private _request: IHotelReservation;
  constructor() {
    this._request = <IHotelReservation>{};
    this._request.stay = <IStay>{};
    this._request.guest = <IGuest>{};
    this._request.guest.address = <IGuestAddress>{};
    this._request.guest.additionalGuests = [];
  }
  withReservation(reservation: Reservation) {
    const stay = useDefault(reservation.stay, new Stay());
    const room = useDefault(reservation.room, new Room());
    const rate = useDefault(reservation.rate, new Rate());
    const guest = useDefault(reservation.guest, new Guest());
    this._request.confirmationNumbers = [<IIhgConfirmationNumber>{ihgConfirmationNumber: reservation.confirmationNumber}];
    this._request.stay.hotelMnemonic = reservation.hotelMnemonic;
    this._request.stay.roomCode = room.code;
    this._request.stay.rateCode = rate.groupCode ? rate.groupCode : rate.code;
    this._request.stay.adults = stay.numberOfAdults;
    this._request.stay.children = stay.numberOfChildren;
    this._request.stay.numberOfRooms = stay.numberOfRooms;
    this._request.stay.checkInDate = DateUtils.toShortISODate(stay.checkInDate);
    this._request.stay.checkOutDate = DateUtils.toShortISODate(stay.checkOutDate);
    this._request.guest.firstName = guest.firstName;
    this._request.guest.lastName = guest.lastName;
    this._request.guest.phoneNumber = guest.phoneNumber;
    this._request.guest.address.addressLine1 = guest.address.addressLine1;
    this._request.guest.address.addressLine2 = guest.address.addressLine2;
    this._request.guest.address.addressLine3 = guest.address.addressLine3;
    this._request.guest.address.countryCode = guest.address.countryCode;
    this._request.guest.address.stateCode = guest.address.stateCode;
    this._request.guest.address.postalCode = guest.address.postalCode;
    this._request.guest.address.city = guest.address.city;
    this._request.guest.additionalGuests = [];
    this.withAdditionalGuests(reservation.additionalGuests);
    return this;
  }
  withCommunicationPreferences(communicationPreferences: CommunicationPreferences) {
    this._request.communicationPreferences = communicationPreferences;
    return this;
  }
  withSpecialNightRate(specialNightRate: SpecialNightRate) {
    if (specialNightRate.typeIndicator && specialNightRate.certificateNumber) {
      this._request.specialNightRate = new SpecialNightRate(specialNightRate.typeIndicator, specialNightRate.certificateNumber);
    }
    return this;
  }
  withAdults (adults: number) {
    this._request.stay.adults = adults;
    return this;
  }
  withChildren (children: number) {
    this._request.stay.children = children;
    return this;
  }
  withNumberOfRooms (numberOfRooms: number) {
    this._request.stay.numberOfRooms = numberOfRooms;
    return this;
  }
  withRate (rateCode: string) {
    this._request.stay.rateCode = rateCode;
    return this;
  }
  withRoom (roomCode: string) {
    this._request.stay.roomCode = roomCode;
    return this;
  }
  withCheckInDate (date: Date) {
    this._request.stay.checkInDate = DateUtils.toShortISODate(date);
    return this;
  }
  withCheckOutDate (date: Date) {
    this._request.stay.checkOutDate = DateUtils.toShortISODate(date);
    return this;
  }
  withStatus (statusType: ReservationStatusType) {
    this._request.status = statusType.toString();
    return this;
  }
  withChanges (changes: Map<ReservationChangeType, any>) {
    changes.forEach((value: any, key: ReservationChangeType) => {
      switch (key) {
        case ReservationChangeType.CHECK_IN_DATE:
          this.withCheckInDate(value);
          break;
        case ReservationChangeType.CHECK_OUT_DATE:
          this.withCheckOutDate(value);
          break;
        case ReservationChangeType.NUMBER_OF_ADULTS:
          this.withAdults(value);
          break;
        case ReservationChangeType.NUMBER_OF_CHILDREN:
          this.withChildren(value);
          break;
        case ReservationChangeType.NUMBER_OF_ROOMS:
          this.withNumberOfRooms(value);
          break;
        case ReservationChangeType.RATE:
          this.withRate(value);
          break;
        case ReservationChangeType.ROOM:
          this.withRoom(value);
          break;
        case ReservationChangeType.ADDITIONAL_GUEST_NAMES:
          this.withAdditionalGuests(value);
          this.removeRateChanges();
          break;
      }
    });
    return this;
  }
  removeRateChanges() {
    // Special request for Sands hotels (MFMVE & MFMPM)
    if (!Hotel.isLMS(this._request.stay.hotelMnemonic) && this._request.guest.additionalGuests.length > 0) {
      delete this._request.rateChanges;
    }
  }
  withReservationRateChanges(rate: Rate, mnemonic: string): ReservationRequestBuilder {
    if (Hotel.isRateChangesNeeded(mnemonic)) {
      // We will allow to do this only for LMS  reservations that require the client to ser the price
      this._request.rateChanges = new Array<IRateChanges>();
      for (const rateChange of rate.originalRateChanges) {
        this._request.rateChanges.push(<IRateChanges> {price: rateChange.price.toFixed(TWO_DECIMALS), startDate: DateUtils.toShortISODate(rateChange.startDate), endDate: DateUtils.toShortISODate(rateChange.endDate)});
      }
    }
    return this;
  }
  withAdditionalGuests(additionalGuests: Array<Guest>) {
    for (const additionalGuest of additionalGuests) {
      this._request.guest.additionalGuests.push(<IAdditionalGuests>{firstName: additionalGuest.firstName, lastName: additionalGuest.lastName});
    }
    return this;
  }
  build (): IHotelReservation {
    return this._request;
  }

}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/builders/reservation-request.builder.ts